import { type JSX, useState } from 'react';
import { Alert, Box, Button, Chip, Link, Stack, Typography } from '@mui/material';

import type { PortalLicense } from '../api.js';
import { buildBundle, type BundleResult, downloadBundle } from '../bundle.js';

export interface LicenseDetailsProps {
  license: PortalLicense;
  rawBlob: string;
  onLookupAnother: () => void;
}

type BundleState =
  | { kind: 'idle' }
  | { kind: 'building' }
  | { kind: 'ready'; result: Extract<BundleResult, { kind: 'ok' }> }
  | { kind: 'error'; message: string };

/**
 * Read-only summary of a fetched license + a "Download license.json" link
 * built from the raw signed blob. Customer drops the downloaded file into
 * their MDM's file-sync / sideload payload alongside config.json (SOTI
 * File Sync, Intune managed configurations, etc.).
 */
export function LicenseDetails({
  license,
  rawBlob,
  onLookupAnother,
}: LicenseDetailsProps): JSX.Element {
  const downloadHref = `data:application/json;charset=utf-8,${encodeURIComponent(rawBlob)}`;
  const [bundle, setBundle] = useState<BundleState>({ kind: 'idle' });

  async function handleBundle(): Promise<void> {
    setBundle({ kind: 'building' });
    const result = await buildBundle({
      tenantId: license.tenantId,
      tier: license.tier,
      rawLicenseBlob: rawBlob,
    });
    if (result.kind === 'ok') {
      setBundle({ kind: 'ready', result });
      // Auto-trigger the download on success; user already clicked once
      // to start the build, no reason to make them click twice.
      downloadBundle(result);
      return;
    }
    setBundle({ kind: 'error', message: result.message });
  }

  return (
    <Box data-testid="license-details">
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>License active</Typography>
        <Chip size="small" label={cap(license.tier)} color={license.tier === 'core' ? 'default' : 'secondary'} />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.75, mb: 2, fontSize: 14 }}>
        <Row label="Tenant ID" value={license.tenantId} mono />
        <Row label="Status" value="Active" />
        <Row label="Expires" value={formatExpiry(license.expiresAt)} />
        <Row label="Features" value={license.features.length === 0 ? '(tier defaults only)' : license.features.join(', ')} />
        <Row label="Signature" value={`${license.signature.slice(0, 16)}…`} mono />
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={() => void handleBundle()} disabled={bundle.kind === 'building'} data-testid="bundle-download">
          {bundle.kind === 'building' ? 'Assembling bundle…' : bundle.kind === 'ready' ? 'Re-download offline bundle' : 'Download offline bundle'}
        </Button>
        <Button variant="outlined" component="a" href={downloadHref} download="license.json" data-testid="license-download">
          Just the license.json
        </Button>
        <Button onClick={onLookupAnother}>Look up another</Button>
      </Stack>

      {bundle.kind === 'ready' && (
        <Alert severity="success" role="status" sx={{ mt: 2 }}>
          Saved <code>{bundle.result.filename}</code>. Drop it into your MDM&apos;s file-sync /
          sideload payload and point a kiosk-mode browser at the unpacked <code>index.html</code>.
        </Alert>
      )}
      {bundle.kind === 'error' && <Alert severity="error" role="alert" sx={{ mt: 2 }}>{bundle.message}</Alert>}
    </Box>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <>
      <Typography component="span" color="text.secondary" variant="body2">{label}</Typography>
      <Typography component="span" variant="body2" {...(mono ? { sx: { fontFamily: 'monospace', wordBreak: 'break-all' } } : {})}>
        {value}
      </Typography>
    </>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatExpiry(epochSec: number | undefined): string {
  if (epochSec === undefined) return 'Never (perpetual)';
  return new Date(epochSec * 1000).toLocaleString();
}
